import React, { useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Upload } from 'lucide-react';

interface FileUploadZoneProps {
  disabled?: boolean;
  isUploading?: boolean;
  onUpload: (files: File[]) => Promise<void> | void;
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  disabled = false,
  isUploading = false,
  onUpload,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);
    try {
      await onUpload(Array.from(files));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
    }
  };

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragging(false);
        if (disabled) return;
        void handleFiles(event.dataTransfer.files);
      }}
      className={`rounded-lg border-2 border-dashed p-6 transition-colors ${
        isDragging ? 'border-primary bg-primary/5' : 'border-border bg-card'
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Upload Files</p>
          <p className="text-xs text-muted-foreground">
            Drag and drop files here, or click to browse. Any file type is supported.
          </p>
          {error && <p className="text-xs text-destructive mt-1">{error}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" disabled={disabled || isUploading}>
            <label className="cursor-pointer">
              <Upload className="mr-2 h-4 w-4" />
              {isUploading ? 'Uploading...' : 'Choose Files'}
              <input
                type="file"
                multiple
                className="hidden"
                onChange={(event) => {
                  void handleFiles(event.target.files);
                  event.currentTarget.value = '';
                }}
              />
            </label>
          </Button>
        </div>
      </div>
    </div>
  );
};
